/**
 * Landing Page Sections Configuration
 *
 * Single source of truth for all subdomain landing page sections.
 * Defines section properties, visibility rules, navigation priorities, and routing.
 *
 * This configuration is used by:
 * - LandingPage.tsx: To generate and render sections
 * - LandingNavbar.tsx: To generate navigation tabs dynamically
 */

/**
 * Section priority levels for navbar display
 * - Priority 1: Always visible in navbar (core navigation)
 * - Priority 2: Shown in "More" dropdown when navbar is crowded (> 7 items)
 */
export type SectionNavPriority = 1 | 2;

/**
 * Data availability context for checking section visibility
 */
export interface SectionAvailabilityData {
  // Location data from GMB
  location?: {
    auto_detected_services?: any[];
    photos?: any[];
    media_items?: {
      photos?: any[];
    };
  };

  // Workspace data
  workspace?: {
    facebook_url?: string;
    instagram_url?: string;
    twitter_url?: string;
    linkedin_url?: string;
    youtube_url?: string;
  };

  // Data availability flags (from database queries)
  hasReviews?: boolean;
  hasPublishedPosts?: boolean;
  hasBlogs?: boolean;
  hasProductsServices?: boolean;

  // Customization settings
  customizations?: any;

  // Helper function for cleaning services
  getCleanedServices?: (services: any[]) => any[];
}

/**
 * Configuration for a single landing page section
 */
export interface LandingPageSectionConfig {
  /** Unique identifier for the section */
  id: string;

  /** Section type (matches component switch case) */
  type: string;

  /** Display title for the section */
  title: string;

  /** Label shown in navigation bar */
  navLabel: string;

  /**
   * Navigation priority
   * 1 = Always visible in navbar
   * 2 = Moves to "More" dropdown when crowded
   */
  navPriority: SectionNavPriority;

  /** Primary route path for this section */
  routePath: string;

  /** Alternative route paths that map to this section */
  routeAliases?: string[];

  /**
   * Check if section has data available to display
   * @returns true if section has data, false otherwise
   */
  hasData: (data: SectionAvailabilityData) => boolean;

  /**
   * Check if section is enabled via customizations
   * @returns true if not explicitly hidden, false if hidden
   */
  isEnabled: (customizations: any) => boolean;
}

/**
 * All landing page sections configuration
 * Order matters: sections render in this order on the page
 */
export const LANDING_PAGE_SECTIONS: LandingPageSectionConfig[] = [
  {
    id: 'hero',
    type: 'hero',
    title: 'Hero',
    navLabel: 'Home',
    navPriority: 1,
    routePath: '/',
    routeAliases: ['hero'],
    hasData: () => true, // Always has data (business name, tagline, etc.)
    isEnabled: (customizations) => customizations?.show_hero !== false,
  },

  {
    id: 'services',
    type: 'services',
    title: 'Our Services',
    navLabel: 'Services',
    navPriority: 1,
    routePath: '/services',
    hasData: (data) => {
      if (!data.location?.auto_detected_services || !data.getCleanedServices) {
        return false;
      }
      const cleanedServices = data.getCleanedServices(data.location.auto_detected_services);
      return cleanedServices.length > 0;
    },
    isEnabled: (customizations) => customizations?.show_services !== false,
  },

  {
    id: 'reviews',
    type: 'reviews',
    title: 'Customer Reviews',
    navLabel: 'Reviews',
    navPriority: 1,
    routePath: '/reviews',
    hasData: (data) => data.hasReviews === true,
    isEnabled: (customizations) => customizations?.show_reviews !== false,
  },

  {
    id: 'blogs',
    type: 'blogs',
    title: 'Our Blog',
    navLabel: 'Blogs',
    navPriority: 2,
    routePath: '/blogs',
    routeAliases: ['blog'],
    hasData: (data) => data.hasBlogs === true,
    isEnabled: (customizations) => customizations?.show_blogs !== false,
  },

  {
    id: 'posts',
    type: 'posts',
    title: 'Latest Updates',
    navLabel: 'Posts',
    navPriority: 2,
    routePath: '/posts',
    routeAliases: ['updates'],
    hasData: (data) => data.hasPublishedPosts === true,
    isEnabled: (customizations) => customizations?.show_posts !== false,
  },

  {
    id: 'products-services',
    type: 'products-services',
    title: 'Products & Services',
    navLabel: 'Products & Services',
    navPriority: 2,
    routePath: '/products-services',
    routeAliases: ['products'],
    hasData: (data) => data.hasProductsServices === true,
    isEnabled: (customizations) => customizations?.show_products_services !== false,
  },

  {
    id: 'appointment',
    type: 'appointment',
    title: 'Book an Appointment',
    navLabel: 'Appointments',
    navPriority: 2,
    routePath: '/appointment',
    hasData: () => true, // Always available (it's a form, not data-dependent)
    isEnabled: (customizations) => customizations?.show_appointment !== false,
  },

  {
    id: 'social',
    type: 'social',
    title: 'Connect With Us',
    navLabel: 'Social',
    navPriority: 2,
    routePath: '/social',
    hasData: (data) => !!(
      data.workspace?.facebook_url ||
      data.workspace?.instagram_url ||
      data.workspace?.twitter_url ||
      data.workspace?.linkedin_url ||
      data.workspace?.youtube_url
    ),
    isEnabled: (customizations) => customizations?.show_social !== false,
  },

  {
    id: 'contact-info',
    type: 'contact-info',
    title: 'Contact Information',
    navLabel: 'Contact Us',
    navPriority: 1,
    routePath: '/contact-us',
    routeAliases: ['contact'],
    hasData: () => true, // Always has data (address, phone, hours, etc.)
    isEnabled: (customizations) => customizations?.show_contact !== false,
  },

  {
    id: 'gallery',
    type: 'gallery',
    title: 'Gallery',
    navLabel: 'Gallery',
    navPriority: 2,
    routePath: '/gallery',
    hasData: (data) => {
      // Check both location.photos and location.media_items.photos
      const hasPhotos = (data.location?.photos?.length || 0) > 0;
      const hasMediaItemsPhotos = (data.location?.media_items?.photos?.length || 0) > 0;
      return hasPhotos || hasMediaItemsPhotos;
    },
    isEnabled: (customizations) => customizations?.show_gallery !== false,
  },
];

/**
 * Helper Functions
 */

/**
 * Determine if a section should be rendered on the page
 * Checks both data availability and customization settings
 */
export const shouldRenderSection = (
  section: LandingPageSectionConfig,
  availabilityData: SectionAvailabilityData
): boolean => {
  const hasData = section.hasData(availabilityData);
  const isEnabled = section.isEnabled(availabilityData.customizations);
  return hasData && isEnabled;
};

/**
 * Get all sections that should be rendered
 * Filters based on data availability and customization settings
 */
export const getVisibleSections = (
  availabilityData: SectionAvailabilityData
): LandingPageSectionConfig[] => {
  return LANDING_PAGE_SECTIONS.filter((section) =>
    shouldRenderSection(section, availabilityData)
  );
};

/**
 * Get sections by navigation priority
 * Used for organizing navbar tabs vs dropdown items
 */
export const getSectionsByPriority = (
  priority: SectionNavPriority,
  availabilityData: SectionAvailabilityData
): LandingPageSectionConfig[] => {
  return LANDING_PAGE_SECTIONS.filter(
    (section) =>
      section.navPriority === priority &&
      shouldRenderSection(section, availabilityData)
  );
};

/**
 * Find section by route path or alias
 * Used for routing and navigation
 */
export const findSectionByRoute = (route: string): LandingPageSectionConfig | undefined => {
  // Normalize route (remove leading slash for comparison)
  const normalizedRoute = route.startsWith('/') ? route : `/${route}`;

  return LANDING_PAGE_SECTIONS.find((section) => {
    // Check primary route path
    if (section.routePath === normalizedRoute) return true;

    // Check aliases
    if (section.routeAliases) {
      return section.routeAliases.some(
        (alias) => `/${alias}` === normalizedRoute || alias === route
      );
    }

    return false;
  });
};

/**
 * Get section configuration by ID
 */
export const getSectionById = (id: string): LandingPageSectionConfig | undefined => {
  return LANDING_PAGE_SECTIONS.find((section) => section.id === id);
};

/**
 * Get section configuration by type
 */
export const getSectionByType = (type: string): LandingPageSectionConfig | undefined => {
  return LANDING_PAGE_SECTIONS.find((section) => section.type === type);
};

/**
 * Check if navbar should use dropdown menu
 * Returns true when Priority 1 + Priority 2 sections together would exceed navbar capacity
 * Takes into account that buttons (Book Now, Call) also take space
 */
export const shouldUseDropdownMenu = (availabilityData: SectionAvailabilityData): boolean => {
  const priority1Count = getSectionsByPriority(1, availabilityData).length;
  const priority2Count = getSectionsByPriority(2, availabilityData).length;

  // If no Priority 2 sections, no dropdown needed
  if (priority2Count === 0) return false;

  // Count buttons that will be shown
  let buttonCount = 0;
  if (availabilityData.customizations?.show_appointment !== false) buttonCount++; // Book Now button
  // Note: Call button is checked separately via phoneNumber prop

  // Maximum direct nav items before needing dropdown
  // Formula: Priority 1 tabs + buttons + some Priority 2 tabs should fit comfortably
  // If Priority 1 + buttons + Priority 2 > 5, use dropdown for Priority 2
  const MAX_ITEMS_BEFORE_DROPDOWN = 5;
  const totalPriority1AndButtons = priority1Count + buttonCount;

  // Use dropdown if adding Priority 2 would make navbar too crowded
  return (totalPriority1AndButtons + priority2Count) > MAX_ITEMS_BEFORE_DROPDOWN;
};

/**
 * Clean and deduplicate services array
 * Removes empty services and duplicates based on title
 */
export const getCleanedServices = (services: any[]): any[] => {
  if (!services || !Array.isArray(services)) return [];

  // Filter out services with empty titles
  const nonEmptyServices = services.filter(service => {
    const title = service?.title || service?.display_name || service?.displayName || service?.name || '';
    return title && title.trim().length > 0;
  });

  // Remove duplicates based on title (case-insensitive)
  const uniqueServices = nonEmptyServices.filter((service, index, self) => {
    const title = (service?.title || service?.display_name || service?.displayName || service?.name || '').toLowerCase().trim();
    return index === self.findIndex(s => {
      const sTitle = (s?.title || s?.display_name || s?.displayName || s?.name || '').toLowerCase().trim();
      return sTitle === title;
    });
  });

  return uniqueServices;
};

/**
 * Prepare complete SectionAvailabilityData for navbar
 * Checks all data availability flags from location and workspace
 */
export const prepareNavbarAvailabilityData = async (
  location: any,
  workspace: any,
  customizations: any,
  supabase: any
): Promise<SectionAvailabilityData> => {
  // Check for reviews
  const { count: reviewsCount } = await supabase
    .from('reviews')
    .select('*', { count: 'exact', head: true })
    .eq('location_id', location.id)
    .gte('rating', 1);
  const hasReviews = (reviewsCount || 0) > 0;

  // Check for published posts
  const { count: postsCount } = await supabase
    .from('posts')
    .select('*', { count: 'exact', head: true })
    .eq('workspace_id', workspace.id)
    .eq('published', true);
  const hasPublishedPosts = (postsCount || 0) > 0;

  // Check for blogs
  const { count: blogsCount } = await supabase
    .from('blogs')
    .select('*', { count: 'exact', head: true })
    .eq('workspace_id', workspace.id)
    .eq('published', true);
  const hasBlogs = (blogsCount || 0) > 0;

  // Check for products/services
  const { count: productsCount } = await supabase
    .from('products_services')
    .select('*', { count: 'exact', head: true })
    .eq('workspace_id', workspace.id);
  const hasProductsServices = (productsCount || 0) > 0;

  return {
    location,
    workspace,
    hasReviews,
    hasPublishedPosts,
    hasBlogs,
    hasProductsServices,
    customizations,
    getCleanedServices,
  };
};

/**
 * Compute all navbar feature flags from availability data
 * Returns flags for hasServices, hasGallery, hasSocial, hasAppointment
 */
export const computeNavbarFeatureFlags = (availabilityData: SectionAvailabilityData) => {
  // Check if Services section has data
  const hasServices = (() => {
    if (!availabilityData.location?.auto_detected_services || !availabilityData.getCleanedServices) {
      return false;
    }
    const cleanedServices = availabilityData.getCleanedServices(availabilityData.location.auto_detected_services);
    return cleanedServices.length > 0;
  })();

  // Check if Gallery section has data (check both photos and media_items.photos)
  const hasGallery =
    (availabilityData.location?.photos?.length || 0) > 0 ||
    (availabilityData.location?.media_items?.photos?.length || 0) > 0;

  // Check if Social section has data
  const hasSocial = !!(
    availabilityData.workspace?.facebook_url ||
    availabilityData.workspace?.instagram_url ||
    availabilityData.workspace?.twitter_url ||
    availabilityData.workspace?.linkedin_url ||
    availabilityData.workspace?.youtube_url
  );

  // Appointment is always available (it's a form, not data-dependent)
  // But check customization setting
  const hasAppointment = availabilityData.customizations?.show_appointment !== false;

  return {
    hasServices,
    hasGallery,
    hasSocial,
    hasAppointment,
  };
};
